import { useEffect, useState, useRef, useCallback } from 'react';
import {
  TrashIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  PencilIcon,
} from '@heroicons/react/24/solid';
import {
  XMarkIcon as XMarkIconOutline,
  CheckIcon as CheckIconOutline,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { updateNote, createNote } from 'views/notepad/moduleServices/notepad_services';
import SpinnerLoad from 'views/notepad/moduleComponents/spinner_load';
import ConfirmModal from 'views/notepad/moduleComponents/ConfirmModal';
import { useClickOutside } from 'services/useClickOutside';
import useScrollLock from 'services/useScrollLock';
import { getLightBackgroundColor } from 'views/notepad/moduleServices/colorUtils';

const EmbeddedNotepadNoteDrawer = ({
  isOpen,
  onClose,
  editNote,
  clusters = [],
  lockedClusterId = null,
  onOptimisticUpdate,
  onDelete,
  onCreateSuccess,
}) => {
  const isCreateMode = isOpen && !editNote;
  const clusterLocked = lockedClusterId != null;
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    cluster_id: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showClusterDropdown, setShowClusterDropdown] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [previousNoteData, setPreviousNoteData] = useState(null);

  const timeoutRef = useRef(null);
  const menuRef = useRef(null);
  const clusterDropdownRef = useRef(null);
  const formDataRef = useRef(formData);
  const handleSubmitRef = useRef(() => {});

  useClickOutside([menuRef], () => setShowMenu(false), showMenu);
  useClickOutside([clusterDropdownRef], () => setShowClusterDropdown(false), showClusterDropdown);
  useScrollLock(isOpen);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        timeoutRef.current = null;
      }, 300);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (editNote) {
      setFormData({
        title: editNote.title || '',
        content: editNote.content || '',
        cluster_id: clusterLocked ? lockedClusterId : (editNote.cluster?.id || null),
      });
      setIsEditMode(false);
    } else {
      setFormData({
        title: '',
        content: '',
        cluster_id: lockedClusterId ?? null,
      });
      setIsEditMode(true);
    }
    setShowDeleteModal(false);
    setShowMenu(false);
    setShowClusterDropdown(false);
  }, [editNote?.id, isOpen, lockedClusterId, clusterLocked]);

  const hasChanges = useCallback(
    (formDataToCheck) => {
      if (isCreateMode) return true;
      if (!editNote) return false;
      const originalTitle = (editNote.title || '').trim();
      const originalContent = (editNote.content || '').trim();
      const originalClusterId = clusterLocked
        ? lockedClusterId
        : (editNote.cluster?.id || null);
      const currentTitle = (formDataToCheck.title || '').trim();
      const currentContent = (formDataToCheck.content || '').trim();
      const currentClusterId = clusterLocked
        ? lockedClusterId
        : (formDataToCheck.cluster_id || null);
      return (
        originalTitle !== currentTitle ||
        originalContent !== currentContent ||
        originalClusterId !== currentClusterId
      );
    },
    [editNote, isCreateMode, clusterLocked, lockedClusterId],
  );

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        e.preventDefault();
        if (isEditMode) {
          const currentFormData = formDataRef.current;
          if (isCreateMode && isEmptyNote(currentFormData)) {
            onClose();
          } else if (editNote && !hasChanges(currentFormData)) {
            setIsEditMode(false);
          } else {
            await handleSubmitRef.current({ preventDefault: () => {} });
          }
        } else {
          onClose();
        }
      }
    };

    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, isOpen, isSubmitting, editNote, hasChanges, onClose, isCreateMode]);

  const isEmptyNote = (data) => !(data.title || '').trim() && !(data.content || '').trim();

  const resolveClusterId = (data) => {
    if (clusterLocked) return lockedClusterId;
    return data.cluster_id || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentFormData = formDataRef.current;
    const payload = {
      title: currentFormData.title,
      content: currentFormData.content,
      cluster_id: resolveClusterId(currentFormData),
    };

    if (isCreateMode) {
      if (isEmptyNote(currentFormData)) {
        onClose();
        return;
      }
      setIsSubmitting(true);
      try {
        const newNote = await createNote(payload);
        const selectedCluster = clusters.find((c) => c.id === payload.cluster_id) || null;
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: newNote.id,
            title: newNote.title,
            content: newNote.content,
            cluster: newNote.cluster || selectedCluster,
            cluster_id: newNote.cluster?.id ?? payload.cluster_id,
            created_at: newNote.created_at,
            updated_at: newNote.updated_at,
          });
        }
        toast.success('Note created successfully!');
        onCreateSuccess?.(newNote);
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to create note');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!editNote) return;

    const editPayload = {
      title: currentFormData.title,
      content: currentFormData.content,
      cluster_id: resolveClusterId(currentFormData),
    };

    if (!hasChanges(currentFormData)) {
      setIsEditMode(false);
      return;
    }
    if (isEmptyNote(editPayload)) {
      toast.error('Note must have a title and/or content.');
      return;
    }

    setIsSubmitting(true);
    try {
      setPreviousNoteData({
        title: editNote.title,
        content: editNote.content,
        updated_at: editNote.updated_at,
      });

      const selectedCluster = editPayload.cluster_id
        ? clusters.find((c) => c.id === editPayload.cluster_id)
        : null;

      const optimisticData = {
        id: editNote.id,
        title: editPayload.title,
        content: editPayload.content,
        updated_at: new Date().toISOString(),
        cluster: selectedCluster || null,
        cluster_id: editPayload.cluster_id,
      };

      if (onOptimisticUpdate) onOptimisticUpdate(optimisticData);
      setIsEditMode(false);

      const updatedNote = await updateNote(editNote.id, editPayload);
      toast.success('Note updated successfully!');

      if (onOptimisticUpdate) {
        onOptimisticUpdate({
          id: editNote.id,
          title: updatedNote.title,
          content: updatedNote.content,
          updated_at: updatedNote.updated_at,
          cluster: updatedNote.cluster || null,
          cluster_id: updatedNote.cluster?.id || null,
        });
      }
      setPreviousNoteData(null);
    } catch (error) {
      if (previousNoteData) {
        setFormData({
          title: previousNoteData.title,
          content: previousNoteData.content,
          cluster_id: clusterLocked ? lockedClusterId : (editNote.cluster?.id || null),
        });
        setIsEditMode(true);
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: editNote.id,
            title: previousNoteData.title,
            content: previousNoteData.content,
            updated_at: previousNoteData.updated_at,
            cluster: editNote.cluster || null,
            cluster_id: editNote.cluster?.id || null,
          });
        }
      }
      toast.error(error.response?.data?.error || 'Failed to save note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  handleSubmitRef.current = handleSubmit;

  const handleEditModeToggle = () => {
    if (editNote) {
      setFormData({
        title: editNote.title || '',
        content: editNote.content || '',
        cluster_id: clusterLocked ? lockedClusterId : (editNote.cluster?.id || null),
      });
    }
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreateMode) {
      onClose();
      return;
    }
    setIsEditMode(false);
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    setIsSubmitting(true);
    try {
      await onDelete(editNote.id);
      toast.success('Note deleted successfully!');
      onClose();
    } catch {
      toast.error('Failed to delete note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getClusterColor = () => {
    const activeClusterId = isEditMode
      ? resolveClusterId(formData)
      : (editNote?.cluster?.id ?? lockedClusterId);
    if (!activeClusterId) return isCreateMode ? null : (editNote?.cluster?.color || null);
    return clusters.find((c) => c.id === activeClusterId)?.color
      || editNote?.cluster?.color
      || null;
  };

  if (!shouldRender || (!editNote && !isCreateMode)) return null;

  const clusterColor = getClusterColor();

  return (
    <>
      <div
        className={`fixed inset-0 z-[110] bg-black transition-opacity duration-300 ${
          isVisible ? 'opacity-30' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 z-[120] h-full w-full max-w-[800px] transform bg-white shadow-xl transition-transform duration-300 ease-in-out md:w-[60%] ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="relative flex h-full flex-col">
          {editNote && (
            <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            title="Delete Note"
            message={`Are you sure you want to delete "${(editNote?.title || '').trim() || 'this note'}"? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
            />
          )}

          <div
            className="flex items-center justify-between border-b bg-white px-4 py-2"
            style={
              clusterColor
                ? { borderBottomColor: clusterColor, borderBottomWidth: '3px' }
                : {}
            }
          >
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h2 className="flex-1 text-center text-xl font-semibold text-gray-800">
              {isCreateMode ? 'New Note' : isEditMode ? 'Edit Note' : 'View Note'}
            </h2>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                    className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                  >
                    <XMarkIconOutline className="h-5 w-5" strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e)}
                    disabled={isSubmitting || (!isCreateMode && !hasChanges(formData))}
                    className="flex items-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <SpinnerLoad size="small" />
                    ) : (
                      <CheckIconOutline className="h-5 w-5" strokeWidth={2.5} />
                    )}
                  </button>
                  {editNote && (
                    <div className="relative" ref={menuRef}>
                      <button
                        type="button"
                        onClick={handleMenuToggle}
                        disabled={isSubmitting}
                        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 top-full z-50 mt-2 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          <button
                            type="button"
                            onClick={handleDeleteClick}
                            disabled={isSubmitting}
                            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {editNote && (
                    <>
                      <button
                        type="button"
                        onClick={handleEditModeToggle}
                        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <div className="relative" ref={menuRef}>
                        <button
                          type="button"
                          onClick={handleMenuToggle}
                          disabled={isSubmitting}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100"
                        >
                          <EllipsisVerticalIcon className="h-5 w-5" />
                        </button>
                        {showMenu && (
                          <div className="absolute right-0 top-full z-50 mt-2 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                            <button
                              type="button"
                              onClick={handleDeleteClick}
                              disabled={isSubmitting}
                              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
            style={
              clusterColor ? { backgroundColor: getLightBackgroundColor(clusterColor) } : {}
            }
          >
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="px-6 pb-2 pt-2">
                {editNote && (
                  <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
                    <span>Updated: {formatDate(editNote.updated_at)}</span>
                    <span>Created: {formatDate(editNote.created_at)}</span>
                  </div>
                )}
                <div className="flex min-w-0 items-center justify-between gap-4">
                  {!isEditMode && editNote && !(editNote?.title || '').trim() ? (
                    <span className="min-w-0 flex-1 text-2xl font-semibold italic text-gray-400">
                      NO TITLE
                    </span>
                  ) : (
                    <input
                      type="text"
                      name="title"
                      value={isEditMode ? formData.title : editNote?.title || ''}
                      onChange={handleChange}
                      disabled={!isEditMode}
                      className={`min-w-0 flex-1 border-none bg-transparent text-2xl font-semibold text-gray-800 placeholder:text-gray-400 focus:outline-none ${
                        !isEditMode ? 'cursor-default' : ''
                      }`}
                      placeholder="Title"
                    />
                  )}
                  {isEditMode && !clusterLocked && (
                    <div className="relative shrink-0" ref={clusterDropdownRef}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowClusterDropdown(!showClusterDropdown);
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full border-2 shadow-sm transition-opacity hover:opacity-80"
                        style={(() => {
                          const selectedCluster = formData.cluster_id
                            ? clusters.find((c) => c.id === formData.cluster_id)
                            : null;
                          const color = selectedCluster?.color || '#9CA3AF';
                          return {
                            backgroundColor: color,
                            borderColor: selectedCluster ? color : '#D1D5DB',
                          };
                        })()}
                      />
                      {showClusterDropdown && (
                        <div className="absolute right-0 top-full z-50 mt-2 max-h-[360px] min-w-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setShowClusterDropdown(false);
                              setFormData((prev) => ({ ...prev, cluster_id: null }));
                            }}
                            className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                              !formData.cluster_id ? 'bg-green-50 text-green-600' : 'text-gray-700'
                            }`}
                          >
                            <div className="h-3 w-3 rounded-full border border-gray-300" />
                            <span>No cluster</span>
                          </button>
                          {clusters.map((cluster) => (
                            <button
                              key={cluster.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowClusterDropdown(false);
                                setFormData((prev) => ({ ...prev, cluster_id: cluster.id }));
                              }}
                              className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                                formData.cluster_id === cluster.id
                                  ? 'bg-green-50 text-green-600'
                                  : 'text-gray-700'
                              }`}
                            >
                              <div
                                className="h-3 w-3 shrink-0 rounded-full"
                                style={{ backgroundColor: cluster.color }}
                              />
                              <span>{cluster.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-1 flex-col overflow-hidden px-4 pb-4">
                <textarea
                  name="content"
                  value={isEditMode ? formData.content : editNote?.content || ''}
                  onChange={handleChange}
                  disabled={!isEditMode}
                  className={`custom-scrollbar min-h-0 flex-1 resize-none overflow-y-auto border-none text-base text-gray-700 placeholder:text-gray-400 focus:outline-none ${
                    !isEditMode ? 'cursor-default bg-transparent' : ''
                  }`}
                  style={(() => {
                    const baseStyle = isEditMode
                      ? {
                          backgroundImage:
                            'repeating-linear-gradient(transparent, transparent 1.4em, #e5e7eb 1.4em, #e5e7eb 1.5em)',
                          backgroundAttachment: 'local',
                          lineHeight: '1.5em',
                          backgroundSize: '100% 1.5em',
                          paddingTop: '0.1em',
                        }
                      : {};
                    return clusterColor
                      ? { ...baseStyle, backgroundColor: getLightBackgroundColor(clusterColor) }
                      : baseStyle;
                  })()}
                  placeholder="Start writing..."
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EmbeddedNotepadNoteDrawer;
